import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { usePrograms, useSection, resolveUrl } from "../lib/useCMS";
import { useInquiry, isInquiryCTA } from "../lib/InquiryContext";
import { slugifyTitle } from "../lib/slug";

const HEADER_FALLBACK = {
  overline: "Programs",
  heading_top: "Proven Ways to Get",
  heading_accent: "Better.",
  description: "From semi-private on-ice clinics to hockey-specific strength and nutrition, programs engineered for defensemen who want long-term, real-world results.",
};

const FALLBACK = [
  {
    id: "fallback-1",
    tag: "On-Ice",
    title: "Semi-Private Instruction",
    description: "Low player-to-coach ratio. Every rep has purpose. Every drill connects to real game habits.",
    price: "Cost Varies",
    features: ["6:1 ratio", "Defensemen-specific", "Video review", "Game-habit drills"],
    cta_label: "BOOK A CLINIC",
    cta_url: "https://book.squareup.com/classes/krpp68j9ccjtzm/location/N7TC3KA55X2D8/classes",
    image_url: "https://images.pexels.com/photos/6468744/pexels-photo-6468744.jpeg?auto=compress&cs=tinysrgb&w=1400",
    featured: true,
  },
];

export const Programs = () => {
  const all = usePrograms(FALLBACK);
  const programs = all.filter((p) => !p.hide_on_landing);
  const offered = all.filter((p) => p.hide_on_landing);
  const h = useSection("programs_header", HEADER_FALLBACK);
  const { openInquiry } = useInquiry();

  return (
    <section id="programs" data-testid="programs-section" className="relative bg-black py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="overline">{h.overline}</p>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase mt-5 leading-[0.9]" data-testid="programs-heading">
              {h.heading_top}
              <br />
              <span className="text-brand-light">{h.heading_accent}</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md">{h.description}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-x-0 gap-y-5 md:gap-5">
          {programs.map((p, idx) => {
            const featured = !!p.featured;
            const wide = !!p.wide && !featured;
            const href = p.cta_url || "#";
            const isInquiry = isInquiryCTA(href);
            const isExternal = !isInquiry && href.startsWith("http");
            const colSpan = featured
              ? "md:col-span-3 md:row-span-2 min-h-[520px]"
              : wide
              ? "md:col-span-3 min-h-[260px]"
              : "md:col-span-2 min-h-[260px]";
            const commonProps = {
              "data-testid": `program-card-${idx}`,
              initial: { opacity: 0, y: 30 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true, amount: 0.15 },
              transition: { duration: 0.6, delay: idx * 0.1 },
              className: `group relative bento-card overflow-hidden flex flex-col text-left w-full ${colSpan}`,
            };
            const inner = (
              <>
                <div className="absolute inset-0">
                  {p.image_url && (
                    <img src={resolveUrl(p.image_url)} alt={p.title} loading="lazy" decoding="async" className="h-full w-full object-cover opacity-30 group-hover:opacity-50 transition-opacity duration-500" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/50" />
                </div>

                <div className="relative p-6 md:p-8 flex-1 flex flex-col">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light">{p.tag}</span>
                  </div>

                  <h3 className={`font-display uppercase leading-[0.95] mt-auto break-words ${featured ? "text-3xl sm:text-4xl md:text-6xl" : "text-2xl md:text-3xl"}`}>
                    {p.title}
                  </h3>

                  <p className={`text-zinc-400 mt-3 ${featured ? "max-w-xl text-base" : "text-sm"}`}>{p.description}</p>

                  {featured && Array.isArray(p.features) && p.features.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {p.features.map((f) => (
                        <span key={f} className="text-[10px] tracking-[0.2em] uppercase font-semibold text-zinc-300 border border-white/15 px-3 py-1.5">
                          {f}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 flex items-center gap-2 text-sm font-display uppercase tracking-wider text-white group-hover:text-brand-light transition-colors">
                    {p.cta_label}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </div>
              </>
            );
            if (isInquiry) {
              return (
                <motion.button
                  key={p.id || idx}
                  type="button"
                  onClick={() => openInquiry(p)}
                  {...commonProps}
                >
                  {inner}
                </motion.button>
              );
            }
            return (
              <motion.a
                key={p.id || idx}
                href={href}
                target={isExternal ? "_blank" : undefined}
                rel="noopener noreferrer"
                {...commonProps}
              >
                {inner}
              </motion.a>
            );
          })}
        </div>

        {offered.length > 0 && (
          <div className="mt-10 md:mt-12 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-zinc-400 text-sm md:text-base" data-testid="programs-also-offering">
            <span>Also offering</span>
            {offered.map((p, i) => {
              const slug = p.slug || slugifyTitle(p.title || "");
              return (
                <span key={p.id || slug || i} className="inline-flex items-center gap-1.5">
                  {i > 0 && <span className="text-zinc-600">·</span>}
                  <Link
                    to={`/programs/${slug}`}
                    className="font-display uppercase tracking-wider text-brand-light hover:text-white underline-offset-4 hover:underline transition-colors inline-flex items-center gap-1.5"
                    data-testid={`programs-also-offering-link-${slug}`}
                  >
                    {p.title}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
